// File-browser model over the flat S3 object index (§5B). The authoritative index
// returns one row per (key × replica); object keys are structured as
// `{network_slug}/{sha-or-uuid}/{filename}` (any depth of folder segments is
// supported). These helpers turn that flat list into a navigable folder hierarchy:
//
//   dedupeObjects(rows) — collapse replica rows into one logical file per s3_key.
//   listDir(files, prefix) — the immediate folders + files directly under a path.
//
// The first path segment is the network slug, so the top level (prefix === []) is
// the set of networks. Pure functions → unit-testable in isolation.

import type { ObjectIndexEntry } from '../api/schemas';

/** One logical object (a leaf file), collapsed from its replica rows. */
export interface ObjectFile {
  s3_key: string;
  /** Filename — the last '/'-separated segment of the key. */
  name: string;
  /** Folder segments leading to the file (everything before `name`). */
  dir: string[];
  size: number;
  is_global: boolean;
  created_at?: string;
  last_seen?: string;
  network_id?: string;
  /** Number of distinct replica rows seen for this key. */
  replicas: number;
}

/** A folder node in the current listing (an aggregate of everything beneath it). */
export interface FolderEntry {
  name: string;
  /** Full path to this folder, including its own name. */
  path: string[];
  /** Files contained anywhere beneath this folder. */
  fileCount: number;
  /** Sum of contained file sizes (largest replica per file). */
  totalSize: number;
}

/** Split an s3 key into its path segments, dropping empties from stray slashes. */
export function keySegments(key: string): string[] {
  return key.split('/').filter((s) => s.length > 0);
}

/**
 * Collapse the (key × replica) index rows into one {@link ObjectFile} per key.
 * `is_global` is OR-ed across replicas; `size` keeps the largest seen; timestamps
 * keep the earliest `created_at` / latest `last_seen`.
 */
export function dedupeObjects(rows: ObjectIndexEntry[]): ObjectFile[] {
  const byKey = new Map<string, ObjectFile>();
  for (const r of rows) {
    const segs = keySegments(r.s3_key);
    if (segs.length === 0) continue;
    const name = segs[segs.length - 1];
    const dir = segs.slice(0, -1);
    const existing = byKey.get(r.s3_key);
    if (!existing) {
      byKey.set(r.s3_key, {
        s3_key: r.s3_key,
        name,
        dir,
        size: r.size ?? 0,
        is_global: !!r.is_global,
        created_at: r.created_at,
        last_seen: r.last_seen,
        network_id: r.network_id,
        replicas: 1,
      });
    } else {
      existing.replicas += 1;
      existing.is_global = existing.is_global || !!r.is_global;
      if ((r.size ?? 0) > existing.size) existing.size = r.size ?? 0;
      if (r.created_at && (!existing.created_at || r.created_at < existing.created_at)) {
        existing.created_at = r.created_at;
      }
      if (r.last_seen && (!existing.last_seen || r.last_seen > existing.last_seen)) {
        existing.last_seen = r.last_seen;
      }
      if (!existing.network_id && r.network_id) existing.network_id = r.network_id;
    }
  }
  return [...byKey.values()];
}

function startsWith(segs: string[], prefix: string[]): boolean {
  if (segs.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    if (segs[i] !== prefix[i]) return false;
  }
  return true;
}

/**
 * Immediate children of `prefix`: subfolders (aggregated) and the files that live
 * directly in this folder. Folders are returned first, both lists sorted by name.
 */
export function listDir(
  files: ObjectFile[],
  prefix: string[],
): { folders: FolderEntry[]; files: ObjectFile[] } {
  const folders = new Map<string, FolderEntry>();
  const here: ObjectFile[] = [];

  for (const f of files) {
    const segs = [...f.dir, f.name];
    if (!startsWith(segs, prefix)) continue;
    const childIdx = prefix.length;
    if (childIdx === segs.length - 1) {
      // The file sits directly inside `prefix`.
      here.push(f);
    } else {
      // A subfolder named segs[childIdx] contains this file (possibly deeper).
      const folderName = segs[childIdx];
      const folderPath = segs.slice(0, childIdx + 1);
      const entry = folders.get(folderName);
      if (entry) {
        entry.fileCount += 1;
        entry.totalSize += f.size;
      } else {
        folders.set(folderName, {
          name: folderName,
          path: folderPath,
          fileCount: 1,
          totalSize: f.size,
        });
      }
    }
  }

  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return {
    folders: [...folders.values()].sort(byName),
    files: here.sort(byName),
  };
}
